import { TypeId } from '@unipile/db';
import { type PublicHostedAuthSession } from '@unipile/provider-shared';
import { Provider, isHttpErrorDetailsPayload, type OauthUrlResponse, type PublicAccount } from '@unipile/types';
import { ProviderProvider, useToast } from '@unipile/ui';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { ProviderIcons, ProviderNames, ProviderPlugins } from '../../config/providers';
import { InitialSyncWaiting } from './InitialSyncWaiting';

const CRITICAL_ERRORS = ['api/account_restricted', 'api/already_exists'];
const PROVIDERS_WITH_INITIAL_SYNC: Provider[] = ['whatsapp', 'imap'];

/**
 * The ProviderWizard renders the provider-specific login flow.
 *
 * Scopes consent is now handled BY EACH PROVIDER using the useScopesConsent hook
 * and ScopesConsentScreen component from @unipile/ui.
 *
 * Each provider decides when to show the consent screen in their flow:
 * - WhatsApp: BEFORE QR code (scanning = immediate connection)
 * - LinkedIn: AFTER credentials, BEFORE submission
 * - etc.
 */
export function ProviderWizard({ provider }: { provider: Provider }) {
  const q = useQueryClient();
  const { toast } = useToast();
  const session = q.getQueryData<PublicHostedAuthSession>(['session']);

  const [waitingForSync, setWaitingForSync] = useState<{
    account_id: TypeId<'account'>;
    app_id: TypeId<'app'>;
    brand_icon_url: string | undefined;
  } | null>(null);

  const onSuccess = (response: PublicAccount | OauthUrlResponse) => {
    // For Oauth supported providers, we redirect user to the provider's oauth screen
    // The redirection made in the following block is handled in /auth/oauth/callback instead
    if (response.object === 'OauthUrl') {
      window.location.href = response.url;
      return;
    }

    // For other providers, we redirect to the redirect_uri provided by the user
    if (!session) {
      Sentry.captureException(new Error('Session not found'));
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again. If the problem persists, contact support.',
        variant: 'destructive',
      });
      return;
    }

    // Check if we should wait for initial sync
    const shouldWaitForSync =
      session.config?.global?.wait_initial_sync && PROVIDERS_WITH_INITIAL_SYNC.includes(response.provider);

    if (shouldWaitForSync) {
      // Show the initial sync waiting screen
      setWaitingForSync({
        account_id: response.id,
        app_id: response.application_id,
        brand_icon_url: session.brand_icon_url,
      });
      return;
    }

    // Otherwise, redirect immediately
    onFlowCompleted(response);
  };

  const onFlowCompleted = (account: PublicAccount) => {
    // For other providers, we redirect to the redirect_uri provided by the user
    const session = q.getQueryData<PublicHostedAuthSession>(['session']);

    if (!session) {
      Sentry.captureException(new Error('Session not found'));
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again. If the problem persists, contact support.',
        variant: 'destructive',
      });
      return;
    }

    const url = new URL(session.redirect_uri);

    url.searchParams.append('account_id', account.id);
    url.searchParams.append('provider', account.provider);
    if (session.state) url.searchParams.append('state', session.state);

    window.location.href = url.toString();
  };

  const onError = (error: unknown) => {
    if (!session) {
      Sentry.captureException(new Error('Session not found'));
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again. If the problem persists, contact support.',
        variant: 'destructive',
      });
      return;
    }

    const url = new URL(session.redirect_uri);

    if (isAxiosError(error)) {
      const response = error.response?.data;

      if (!isHttpErrorDetailsPayload(response)) {
        toast({
          title: 'Error',
          description: 'Something went wrong. Please try again. If the problem persists, contact support.',
          variant: 'destructive',
        });
        return;
      }

      // If the error is not critical (ex invalid credentials), we capture it and show a toast
      if (!CRITICAL_ERRORS.includes(response.type)) {
        toast({
          title: response.title,
          description: response.detail || 'Something went wrong. Please try again. If the problem persists, contact support.',
          variant: 'destructive',
        });
        return;
      }

      // If the error is critical, we redirect to the error page
      url.searchParams.append('error_title', response.title);
      url.searchParams.append('error_type', response.type);
      url.searchParams.append('error_detail', response.detail || '');
    }

    window.location.href = url.toString();
  };

  const onCancel = () => {
    if (!session) {
      window.history.back();
      return;
    }

    const url = new URL(session.redirect_uri);

    url.searchParams.append('error_type', 'canceled');
    url.searchParams.append('error_title', 'Canceled');
    url.searchParams.append('error_detail', 'Authentication canceled by the user.');
    if (session.state) url.searchParams.append('state', session.state);

    window.location.href = url.toString();
  };

  const plugin = ProviderPlugins[provider];
  const Wizard = plugin.Component;

  if (waitingForSync) return <InitialSyncWaiting {...waitingForSync} onComplete={onFlowCompleted} />;

  return (
    <ProviderProvider
      provider={provider}
      provider_name={ProviderNames[provider]}
      provider_icon={ProviderIcons[provider]}
      product_icons={plugin.productIcons}
    >
      <Wizard onSuccess={onSuccess} onError={onError} onCancel={onCancel} />
    </ProviderProvider>
  );
}
